import { memo, useMemo } from "react";

import { useProduct } from "@shopify/hydrogen-react";
import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { type BadgeType } from "@components/Badge";
import { ProductGalleryGridHeroImage } from "@react/sections/ProductHero/ProductGalleryGrid/ProductGalleryGridHeroImage.view";
import { ProductGalleryGridImage } from "@react/sections/ProductHero/ProductGalleryGrid/ProductGalleryGridImage.view";
import { ProductGalleryGridVideo } from "@react/sections/ProductHero/ProductGalleryGrid/ProductGalleryGridVideo.view";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import {
  formatFullyQualifiedUrl,
  formatProductVariant,
} from "@root/tablescape/utilities";
import {
  type CartMediaVideo,
  type CartMediaImage,
  type ProductHeroSettings,
} from "@tablescape/state";

type Props = {
  isHero: boolean;
  lazyLoadImages: boolean;
  lcpMedia: ProductHeroSettings["productGalleryCarousel"]["lcpMedia"];
  media:
    | (CartMediaImage & { mediaContentType: "IMAGE" })
    | (CartMediaVideo & { mediaContentType: "VIDEO" });
  videoLabel: string;
};

const ProductGalleryGridMediaBase = ({
  isHero,
  lazyLoadImages,
  lcpMedia,
  media,
  videoLabel,
}: Props) => {
  const { product, selectedVariant } = useProduct();
  const {
    productGalleryGrid: { awardBadgeImage },
  } = useSectionSettings<ProductHeroSettings>();

  const formattedSelectedVariant = useMemo(
    () => formatProductVariant(selectedVariant),
    [selectedVariant]
  );

  const resolvedBadgeType = useMemo<Maybe<BadgeType>>(() => {
    switch (formattedSelectedVariant.badgeType) {
      case "sage":
        return "tertiary";
      case "brick":
        return "quaternary";
      case "bright-blue":
        return "senary";
      default:
        return "primary";
    }
  }, [formattedSelectedVariant]);

  const resolvedBadgeImage = useMemo(() => {
    if (formattedSelectedVariant.badgeImage) {
      return {
        alt: formattedSelectedVariant.badgeImage.alt,
        src: formattedSelectedVariant.badgeImage.image.url,
      };
    }

    if (awardBadgeImage.src) {
      return {
        alt: awardBadgeImage.alt,
        src: formatFullyQualifiedUrl(awardBadgeImage.src),
      };
    }
  }, [formattedSelectedVariant, awardBadgeImage]);

  const formattedAlternateImage = useMemo(() => {
    if (formattedSelectedVariant.alternateImage) {
      return {
        alt: formattedSelectedVariant.alternateImage.alt,
        src: formatFullyQualifiedUrl(
          formattedSelectedVariant.alternateImage.image.url
        ),
      };
    }
  }, [formattedSelectedVariant]);

  const videoAriaLabel = useMemo(
    () => `${videoLabel} ${product.title}`,
    [product]
  );

  const isLCPRender = useMemo(() => {
    if (
      !media ||
      !lcpMedia.sources?.length ||
      media.mediaContentType !== "IMAGE"
    ) {
      return false;
    }

    const url = new URL(media.image.url);
    const path = url.pathname.split("/").reverse()[0];

    return lcpMedia.sources[0].src.includes(path);
  }, [lcpMedia, media]);

  return media.mediaContentType === "IMAGE" ? (
    isHero ? (
      <ProductGalleryGridHeroImage
        alternateImage={formattedAlternateImage}
        badgeImage={resolvedBadgeImage}
        badgeText={formattedSelectedVariant.badgeText}
        badgeType={resolvedBadgeType}
        image={media.image}
        isLCPRender={isLCPRender}
        lazyLoadImages={lazyLoadImages}
        lcpMedia={lcpMedia}
      />
    ) : (
      <ProductGalleryGridImage
        image={media.image}
        lazyLoadImages={lazyLoadImages}
      />
    )
  ) : (
    <ProductGalleryGridVideo ariaLabel={videoAriaLabel} video={media} />
  );
};

export const ProductGalleryGridMedia = memo(ProductGalleryGridMediaBase);
